/// <amd-module name="Dashboards/Common/UserPermissions" />
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";

export interface IDashboardsUserPermissionService extends IVssService {
    CanReadDashboards(): boolean;
    CanCreateDashboards(): boolean;
    CanEditDashboards(): boolean;
    CanDeleteDashboards(): boolean;
    CanManagePermissionForDashboards(): boolean;
}

interface WrappedValue {
    value: number;
}

export class DashboardsUserPermissionService extends VssService implements IDashboardsUserPermissionService {
    public CanReadDashboards(): boolean {
        return this.hasModernRead() || this.hasModernManagePermissions();
    }

    public CanCreateDashboards(): boolean {
        return this.hasModernCreate() || this.hasModernManagePermissions();
    }

    public CanEditDashboards(): boolean {
        return this.hasModernEdit() || this.hasModernManagePermissions();
    }

    public CanDeleteDashboards(): boolean {
        return this.hasModernDelete() || this.hasModernManagePermissions();
    }

    public CanManagePermissionForDashboards(): boolean {
        return this.hasModernManagePermissions();
    }

    private getPermissionsForUser(): number {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const providerState = contributionService.getData<WrappedValue>("ms.vss-dashboards-web.dashboards-team-context-data-provider");
        return providerState != null ? providerState.value : 0;
    }

    private hasLegacyRead(): boolean {
        return (this.getPermissionsForUser() & Permissions.Read) !== 0;
    }

    private hasLegacyEdit(): boolean {
        return (this.getPermissionsForUser() & Permissions.Edit) !== 0;
    }

    private hasLegacyManage(): boolean {
        return (this.getPermissionsForUser() & Permissions.Manage) !== 0;
    }

    private hasLegacyManagePermissions(): boolean {
        return (this.getPermissionsForUser() & Permissions.ManagePermissions) !== 0;
    }

    private hasModernRead(): boolean {
        return (this.getPermissionsForUser() & DashboardsPermissions.Read) !== 0;
    }

    private hasModernCreate(): boolean {
        return (this.getPermissionsForUser() & DashboardsPermissions.Create) !== 0;
    }

    private hasModernEdit(): boolean {
        return (this.getPermissionsForUser() & DashboardsPermissions.Edit) !== 0;
    }

    private hasModernDelete(): boolean {
        return (this.getPermissionsForUser() & DashboardsPermissions.Delete) !== 0;
    }

    private hasModernManagePermissions(): boolean {
        return (this.getPermissionsForUser() & DashboardsPermissions.ManagePermissions) !== 0;
    }
}

enum DashboardsPermissions {
    Read = 1,
    Create = 2,
    Edit = 4,
    Delete = 8,
    ManagePermissions = 16
}

enum Permissions {
    Read = 1,
    Edit = 2,
    Manage = 4,
    ManagePermissions = 8
}

Services.add("IDashboardsUserPermissionService", { serviceFactory: DashboardsUserPermissionService });
